import { memo, useMemo } from "react";
import { Avatar, Box, Paper, Stack, Typography } from "@mui/material";
import { DateDisplay } from "../DateDisplay";
import { EntityChip } from "../EntityChip";
import { EntityChips } from "../EntityChips";
import { formatTransactionCurrency } from "../../logic/currency-logic";
import { isNegative } from "../../logic/money";
import { useRegistry } from "../../context/RegistryContext";
import { RowActionButtons } from "./RowActionButtons";
import { useRowEdit, type EditSession } from "./editSession";
import { deriveRowView } from "./rowView";
import {
  counterpartyMarker,
  resolveCounterparty,
  type AccountNumberIndex,
} from "../../logic/counterparty";
import { DerivedAccountChip } from "../DerivedAccountChip";
import type {
  AutoTransactionRuleDto,
  CurrencyFormat,
  TransactionId,
  TransactionListDto,
} from "../../api/types";

export type MobileTransactionCardProps = {
  transaction: TransactionListDto;
  session: EditSession;
  matchingRules: AutoTransactionRuleDto[] | undefined;
  currencyFormat: CurrencyFormat;
  /// Built once by the list, so the memoized card keeps its identity.
  accountNumberIndex: AccountNumberIndex;
  onViewDetails: (id: TransactionId) => void;
  onCreateRule: (tx: TransactionListDto) => void;
  onOpenSuggestions: (
    event: React.MouseEvent<HTMLElement>,
    txId: TransactionId,
  ) => void;
  onOpenAppliedRules: (
    event: React.MouseEvent<HTMLElement>,
    tx: TransactionListDto,
  ) => void;
};

export const MobileTransactionCard = memo(
  ({
    transaction,
    session,
    matchingRules,
    currencyFormat,
    accountNumberIndex,
    onViewDetails,
    onCreateRule,
    onOpenSuggestions,
    onOpenAppliedRules,
  }: MobileTransactionCardProps) => {
    const { getSource } = useRegistry().withHidden;
    const edit = useRowEdit(session, transaction.id);
    const { values, pending } = useMemo(
      () => deriveRowView(transaction, edit),
      [transaction, edit],
    );

    // Read off the effective values so a pending payTo/amount edit is reflected.
    const counterparty = resolveCounterparty(
      values.payToAccount,
      accountNumberIndex,
    );
    const marker = counterpartyMarker(values.amount, counterparty);

    const source = getSource(values.source);
    const displayValue = values.description || "";
    const isPlaceholder = !displayValue;
    const text = isPlaceholder ? values.merchant : displayValue;

    return (
      <Paper
        sx={{
          p: 2,
          cursor: "pointer",
          bgcolor: pending.size > 0 ? "action.selected" : undefined,
        }}
        onClick={() => onViewDetails(transaction.id)}
        elevation={1}
      >
        <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start" }}>
          {source ? (
            <EntityChip type="source" name={source.name} iconOnly />
          ) : (
            <Avatar>?</Avatar>
          )}

          <Box sx={{ flexGrow: 1 }}>
            <Stack
              direction="row"
              sx={{ justifyContent: "space-between", alignItems: "flex-start" }}
            >
              <Box sx={{ maxWidth: "70%" }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  <DateDisplay date={values.datePaid} />
                </Typography>
                <Typography
                  variant="body1"
                  color={isPlaceholder ? "text.secondary" : "text.primary"}
                  sx={{
                    fontWeight: isPlaceholder ? "normal" : "medium",
                    mt: 0.5,
                    lineHeight: 1.2,
                    fontStyle: isPlaceholder ? "italic" : "normal",
                  }}
                >
                  {text}
                </Typography>
                {/* Its own line: the card has vertical room, so the marker
                    wraps rather than competing with the description for
                    width, and stays a step below it in emphasis. */}
                {marker && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "text.disabled",
                      lineHeight: 1.4,
                    }}
                  >
                    {marker.arrow} {marker.name}
                  </Typography>
                )}
              </Box>
              <Stack sx={{ alignItems: "flex-end" }}>
                {/* Negatives coloured as in Monthly Summary, matching the
                    desktop AmountCell. */}
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: "bold",
                    color: isNegative(values.amount) ? "error.main" : undefined,
                  }}
                >
                  {formatTransactionCurrency(values.amount, currencyFormat)}
                </Typography>
                {values.foreignAmount && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: isNegative(values.foreignAmount)
                        ? "error.main"
                        : "text.secondary",
                    }}
                  >
                    {formatTransactionCurrency(
                      values.foreignAmount,
                      currencyFormat,
                    )}
                  </Typography>
                )}
                <RowActionButtons
                  transaction={transaction}
                  matchingRules={matchingRules}
                  onCreateRule={onCreateRule}
                  onOpenSuggestions={onOpenSuggestions}
                  onOpenAppliedRules={onOpenAppliedRules}
                />
              </Stack>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 1.5, flexWrap: "wrap", gap: 1 }}
            >
              {values.expenseType && (
                <EntityChip
                  type="expenseType"
                  name={values.expenseType}
                  variant="filled"
                  size="small"
                  iconOnly
                />
              )}
              <DerivedAccountChip value={values.derivedAccount} size="small" />
              {values.group && (
                <EntityChip
                  type="group"
                  name={values.group}
                  variant="filled"
                  size="small"
                  iconOnly
                />
              )}
              {values.categories && values.categories.length > 0 && (
                <EntityChips
                  type="category"
                  names={values.categories}
                  size="small"
                />
              )}
            </Stack>
          </Box>
        </Stack>
      </Paper>
    );
  },
);

MobileTransactionCard.displayName = "MobileTransactionCard";
